import {
  Absence,
  AbsenceReason,
  AbsenceUserHash,
  AbsenceUserSchedule,
  AbsenceUserScheduleDay,
  AbsenceUserScheduleShift
} from "./absence.interface";
import { PAUtil, WeekDay } from "../_components/planning-assistant/classes/util";
import { PATimeControl } from "../_components/planning-assistant/singletons/pa-time-control";
import { AbsenceService } from "../_services/absence.service";
import { Injectable } from "@angular/core";

export class AbsenceUser {
  constructor(
    public readonly absenceData: AbsenceUserHash
  ) {
  }

  hasShiftOnWeekdayWithFallback(weekday: WeekDay, after_day_ts: number): boolean {
    if (weekday == 'saturday') return true; // exception: every technician has a possible shift on saturday
    if (this.absenceData.schedules.length) {
      return this.hasShiftOnWeekday(weekday, after_day_ts);
    } else {
      PATimeControl.Instance.workDays.includes(weekday)
    }
  }

  getSummedShiftMinutesOnWeekdayWithFallback(weekday: WeekDay, after_day_ts: number): number {
    if (weekday == 'saturday') return 480; // exception: every technician has a possible shift on saturday
    if (this.absenceData.schedules.length) {
      return this.getSummedShiftMinutesOnWeekday(weekday, after_day_ts)
    } else {
      return PATimeControl.Instance.workDays.includes(weekday) ? 480 : 0
    }
  }

  private hasShiftOnWeekday(weekday: WeekDay, after_day_ts: number): boolean {
    return this.getSummedShiftMinutesOnWeekday(weekday, after_day_ts) > 0;
  }

  private getSummedShiftMinutesOnWeekday(weekday: WeekDay, after_day_ts: number): number {
    const shifts = this.getShiftsOnWeekday(weekday, after_day_ts);
    return shifts ? shifts.reduce(
      (sum: number, shift: AbsenceUserScheduleShift) => {
        const shift_minutes = PATimeControl.Instance.timeStringToMinutes(shift.end) - PATimeControl.Instance.timeStringToMinutes(shift.start)
        return shift_minutes + sum;
      },
      0
    ) : 0
  }

  private getShiftsOnWeekday(weekday: WeekDay, after_day_ts: number): AbsenceUserScheduleShift[] | undefined {
    return this.getShiftsOnDayIdx((PATimeControl.Instance.weekDays.indexOf(weekday) + 1) % 7, after_day_ts);
  }

  private getSchedulesAfterDayTimestamp(ts: number): AbsenceUserSchedule | undefined {
    if (this.absenceData.schedules.length === 0) return
    const ordered_schedules = [...this.absenceData.schedules.sort(
      (sc_a, sc_b) => {
        return new Date(sc_a.start).getTime() > new Date(sc_b.start).getTime() ? 1 : -1
      }
    )]
    let current_schedule = ordered_schedules.splice(0, 1)[0];
    for (const schedule of ordered_schedules) {
      if (new Date(schedule.start).getTime() < ts) {
        current_schedule = schedule;
      }
    }
    return current_schedule
  }

  private getShiftsOnDayIdx(idx: number, after_day_ts: number): AbsenceUserScheduleShift[] | undefined {
    const current_schedule = this.getSchedulesAfterDayTimestamp(after_day_ts)
    if (current_schedule) {
      const schedule_day = current_schedule.days[0][idx.toString()] as AbsenceUserScheduleDay
      if (schedule_day?.active) {
        return schedule_day.shift
      }
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class AbsenceCalendar {

  public readonly reasonMap = new Map<string, AbsenceReason>();
  public readonly absenceMap = new Map<string, Absence[]>(); // key: 'YYYY-MM-DD'
  public readonly absenceUserMap = new Map<string, AbsenceUser>();

  public absenceUserHashes: AbsenceUserHash[] = []

  constructor(
    private absenceService: AbsenceService
  ) {
  }

  async loadAbsenceUsers(): Promise<void> {
    return await new Promise<void>((resolve) => {
      this.absenceService.getUsers().subscribe(
        data => {
          this.absenceUserHashes = data;
          for (const user_hash of data) {
            this.absenceUserMap.set(user_hash.id, new AbsenceUser(user_hash))
          }
          resolve()
        },
        error => {
          console.log(error)
          resolve()
        }
      )
    })
  }

  // date_start & date_end format: YYYY-MM-DD
  async loadAbsencesBetweenDates(date_start: string, date_end?: string) {
    const loading_dates = this.generateAllDatesBetweenDates(date_start, date_end || date_start, true);
    if (loading_dates.length) {
      return await new Promise<void>((resolve) => {
        this.absenceService.getAbsencesInDateInterval(loading_dates[0], loading_dates[loading_dates.length - 1]).subscribe(
          data => {
            for (const absence of data) {
              const date_start = PATimeControl.Instance.dateToDatestring(new Date(absence.start), true, true, '-')
              const date_end = PATimeControl.Instance.dateToDatestring(new Date(absence.end), true, true, '-')
              const absence_dates = this.generateAllDatesBetweenDates(date_start, date_end)
              for (const date of absence_dates) {
                if (!this.absenceMap.has(date)) this.absenceMap.set(date, [])
                const day_absences = this.absenceMap.get(date)
                if (
                  !day_absences.find(abs => abs._id == absence._id) &&
                  absence_dates.indexOf(date) != absence_dates.length - 1 // skip adding the last date, because the end-date is the first working date
                ) {
                  day_absences.push(absence)
                  this.initReason(absence.reasonId)
                }
              }
            }
            resolve()
          },
          error => {
            console.log(error)
            resolve()
          }
        )
      })
    }

  }

  private generateAllDatesBetweenDates(date_start: string, date_end: string, skip_already_loaded_dates?: boolean): string[] {
    const res: string[] = []
    let current_ts = PATimeControl.Instance.dateStringToTimestamp(date_start, true, true)
    const ts_end = PATimeControl.Instance.dateStringToTimestamp(date_end, true, true)
    while (current_ts <= ts_end) {
      res.push(PATimeControl.Instance.timestampToUTCDatestring(current_ts, true, '-'))
      current_ts += 24 * 60 * 60 * 1000
    }

    return skip_already_loaded_dates ? res.filter(date => !this.absenceMap.has(date)) : res
  }

  initReason(id: string): void {
    if (!this.reasonMap.has(id)) {
      this.reasonMap.set(id, null)
      this.absenceService.getAbsenceReason(id).subscribe(
        reason => {
          this.reasonMap.set(id, reason)
        },
        error => {
          this.reasonMap.set(id, {id: '', name: 'Ladefehler', requiresApproval: false})
        }
      )
    }
  }

  async waitForReason(id: string): Promise<void> {
    if (!this.reasonMap.has(id)) {
      this.initReason(id)
    }
    await PAUtil.sleepUntilConditionBreaks(
      () => {
        return !this.reasonMap.get(id)
      }
    )
  }

  getAbsenceUserIdByMail(email: string): string {
    if (email) return this.absenceUserHashes.find(auh => auh.email.toLowerCase() == email.toLowerCase())?.id
  }

  getAbsenceUserIdByFullName(name: string): string {
    if (name) return this.absenceUserHashes.find(auh => auh.name.toLowerCase().replaceAll(' ', '') == name.toLowerCase().replaceAll(' ', ''))?.id
  }

  getLoadedUserAbsencesOnDate(user_id: string, date_string: string) {
    if (!this.absenceMap.has(date_string)) this.absenceMap.set(date_string, [])
    return this.absenceMap.get(date_string).filter(absence => absence.assignedToId == user_id)
  }
}
